import { Injectable, InjectionToken, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

/** Response envelope from the recruitment cv-intake endpoint. The
 *  correlationId is what SignalR events later carry so consumers can
 *  match the intake to the eventual application arrival. */
export interface CvIntakeResponse {
  correlationId: string;
}

/** Config the consuming app supplies at bootstrap. Kept minimal —
 *  the lib doesn't know about environment shape, only what it needs
 *  to reach the intake endpoint. Same pattern as ORG_CONTEXT_CONFIG
 *  in @org/shared. */
export interface CvUploadConfig {
  /** Absolute base URL of the recruitment API — e.g.
   *  `https://api.dev.ka-boom.app/api/recruitment`. The service appends
   *  `/jobpostings/{jobId}/cv-intake` to it. */
  recruitmentApiUrl: string;
}

/**
 * Factory throws with an actionable message rather than the framework's
 * generic NullInjectorError, so a missing provider fails at bootstrap
 * with a clear pointer to the provider snippet below.
 */
export const CV_UPLOAD_CONFIG = new InjectionToken<CvUploadConfig>(
  'CV_UPLOAD_CONFIG',
  {
    factory: () => {
      throw new Error(
        'CV_UPLOAD_CONFIG is not provided. In your app config, add:\n' +
        "  { provide: CV_UPLOAD_CONFIG, useValue: { recruitmentApiUrl: '<url>' } }",
      );
    },
  },
);

/**
 * Shared CV-upload data-plane. Talks to the recruitment jobpostings
 * cv-intake endpoint, which stores the file in blob storage and raises
 * a cv-intake Service Bus event for the cv-processor worker to parse
 * via Affinda and create the candidate + application.
 *
 * The endpoint's URL is scoped under jobpostings/{jobId}/cv-intake
 * because every CV is submitted against a specific role. That's a
 * routing detail of the API, not a UX constraint of this service —
 * callers just supply the target jobId.
 *
 * Base URL is injected via CV_UPLOAD_CONFIG so this lib stays app-
 * agnostic — no reach-in imports from any app's environment file.
 */
@Injectable({ providedIn: 'root' })
export class CvUploadService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(CV_UPLOAD_CONFIG);

  uploadCv(
    jobId: string,
    file: File,
    extras: CvUploadExtras = {},
  ): Observable<CvIntakeResponse> {
    const form = new FormData();
    form.append('file', file, file.name);
    appendIfSet(form, 'candidateName',    extras.candidateName);
    appendIfSet(form, 'candidateEmail',   extras.candidateEmail);
    appendIfSet(form, 'phoneNumber',      extras.phoneNumber);
    appendIfSet(form, 'currentSalary',    extras.currentSalary);
    appendIfSet(form, 'desiredSalaryMin', extras.desiredSalaryMin);
    appendIfSet(form, 'desiredSalaryMax', extras.desiredSalaryMax);
    appendIfSet(form, 'currency',         extras.currency);

    return this.http.post<CvIntakeResponse>(
      `${this.config.recruitmentApiUrl}/jobpostings/${jobId}/cv-intake`,
      form,
    );
  }
}

/** Extra fields the recruiter may supply alongside the CV file. All optional —
 *  send only what you have and the backend applies them to the created candidate. */
export interface CvUploadExtras {
  candidateName?: string;
  candidateEmail?: string;
  phoneNumber?: string;
  currentSalary?: number | null;
  desiredSalaryMin?: number | null;
  desiredSalaryMax?: number | null;
  currency?: string;
}

function appendIfSet(form: FormData, key: string, value: string | number | null | undefined): void {
  if (value === null || value === undefined) return;
  const text = typeof value === 'string' ? value : String(value);
  if (text === '') return;
  form.append(key, text);
}
